import React, { useState } from 'react';
import ToolLayout from '../components/ToolLayout';

const UrlEncoder: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');

  const encodeUrl = () => {
    setError('');
    setSuccess('');
    
    if (!input.trim()) {
      setError('Vui lòng nhập URL hoặc text để encode');
      return;
    }

    try {
      const encoded = encodeURIComponent(input);
      setOutput(encoded);
      setSuccess('Text đã được URL encode thành công! 🌐');
    } catch (err) {
      setError('Không thể encode: ' + (err as Error).message);
      setOutput('');
    }
  };

  const decodeUrl = () => {
    setError('');
    setSuccess('');
    
    if (!input.trim()) {
      setError('Vui lòng nhập URL encoded string để decode');
      return;
    }

    try {
      const decoded = decodeURIComponent(input);
      setOutput(decoded);
      setSuccess('URL đã được decode thành công! ✅');
    } catch (err) {
      setError('URL encoded string không hợp lệ: ' + (err as Error).message);
      setOutput('');
    }
  };

  const handleProcess = () => {
    if (mode === 'encode') {
      encodeUrl();
    } else {
      decodeUrl();
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
    setSuccess('');
  };

  const copyToClipboard = async () => {
    if (output) {
      try {
        await navigator.clipboard.writeText(output);
        setSuccess('Đã copy vào clipboard!');
      } catch (err) {
        setError('Không thể copy vào clipboard');
      }
    }
  };

  const swapInputOutput = () => {
    if (output) {
      setInput(output);
      setOutput('');
      setMode(mode === 'encode' ? 'decode' : 'encode');
      setError('');
      setSuccess('');
    }
  };

  const parseQuery = () => {
    if (!input.trim()) {
      setError('Vui lòng nhập URL với query string');
      return;
    }

    try {
      const url = new URL(input.includes('://') ? input : 'https://example.com' + (input.startsWith('/') ? '' : '/') + input);
      const params: { [key: string]: string } = {};
      
      url.searchParams.forEach((value, key) => {
        params[key] = value;
      });

      const formatted = JSON.stringify(params, null, 2);
      setOutput(formatted);
      setSuccess('Query parameters đã được parse thành công! 🔍');
      setError('');
    } catch (err) {
      setError('URL không hợp lệ hoặc không có query parameters');
      setOutput('');
    }
  };

  return (
    <ToolLayout 
      title="URL Encoder/Decoder" 
      description="Encode, decode URLs và parse query strings một cách dễ dàng"
    >
      {/* Mode Selection */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              value="encode"
              checked={mode === 'encode'}
              onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
            />
            <span>🔒 Encode (Text → URL encoded)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="radio"
              value="decode"
              checked={mode === 'decode'}
              onChange={(e) => setMode(e.target.value as 'encode' | 'decode')}
            />
            <span>🔓 Decode (URL encoded → Text)</span>
          </label>
        </div>
      </div>

      <div className="input-group">
        <label className="input-label">
          {mode === 'encode' ? 'Nhập text/URL để encode:' : 'Nhập URL encoded string để decode:'}
        </label>
        <textarea
          className="input-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={mode === 'encode' 
            ? 'Nhập text hoặc URL của bạn ở đây...\n\nVí dụ: https://example.com/search?q=hello world&lang=vi' 
            : 'Nhập URL encoded string ở đây...\n\nVí dụ: https%3A//example.com/search%3Fq%3Dhello%20world%26lang%3Dvi'
          }
          style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace' }}
        />
      </div>

      <div className="button-group">
        <button className="action-button" onClick={handleProcess}>
          {mode === 'encode' ? '🔒 URL Encode' : '🔓 URL Decode'}
        </button>
        <button className="action-button" onClick={parseQuery}>
          🔍 Parse Query String
        </button>
        {output && (
          <button className="action-button" onClick={swapInputOutput}>
            ⇅ Swap & Switch Mode
          </button>
        )}
        <button className="action-button secondary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {output && (
        <div className="input-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label className="input-label">
              Kết quả:
            </label>
            <button className="action-button" onClick={copyToClipboard}>
              📋 Copy
            </button>
          </div>
          <textarea
            className="input-textarea"
            value={output}
            readOnly
            style={{ 
              fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
              textAlign: 'left'
            }}
          />
        </div>
      )}

      {/* Info Section */}
      <div className="url-info-section">
        <h4 className="url-info-title">
          🌐 URL Tools:
        </h4>
        <div className="url-info-content">
          <p>
            <strong>🔒 URL Encode:</strong> Chuyển đổi text thành URL-safe format
          </p>
          <p>
            <strong>🔓 URL Decode:</strong> Chuyển đổi URL encoded string về text gốc
          </p>
          <p>
            <strong>🔍 Parse Query:</strong> Phân tích query parameters thành JSON
          </p>

          <strong>Ký tự được encode:</strong>
          <div className="url-char-examples">
            <div>Space ( ) → %20</div>
            <div>& → %26, ? → %3F, = → %3D</div>
            <div>@ → %40, # → %23, % → %25</div>
            <div>+ → %2B, / → %2F, : → %3A</div>
          </div>
        </div>
      </div>

      <style>{`
        .url-info-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 2px solid var(--accent-primary);
        }

        .url-info-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
        }

        .url-info-content {
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .url-info-content p {
          margin: 0 0 0.5rem 0;
        }

        .url-char-examples {
          margin: 0.5rem 0;
          padding: 0.75rem;
          background: var(--card-bg);
          border-radius: 6px;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 0.85rem;
          color: var(--text-primary);
        }

        .url-char-examples div {
          margin-bottom: 0.25rem;
        }

        .url-char-examples div:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </ToolLayout>
  );
};

export default UrlEncoder;






